import { useEffect } from 'react';

import { useApi } from '@paradigm/api/src/fetch-api';

import {
  BillingMe,
  CreditPack,
  getBillingMe,
  getCreditPacks,
} from '#/api/billing';

export interface BillingMeState {
  /** ``null`` while loading or if the fetch failed. */
  readonly me: BillingMe | null;
  readonly packs: readonly CreditPack[];
  /** Non-null when either the ``me`` or ``packs`` request failed. */
  readonly initError: string | null;
}

/**
 * Fetches the two resources the billing panel needs (``/billing/me/``
 * and ``/billing/credit-packs/``) in parallel and returns a combined
 * state. Co-locates the loading-state derivation so the panel
 * component itself stays focused on action handlers and rendering.
 */
export default function useBillingMe(): BillingMeState {
  const [meState, fetchMe] = useApi(getBillingMe);
  const [packsState, fetchPacks] = useApi(getCreditPacks);

  useEffect(() => {
    fetchMe({});
    fetchPacks({});
  }, [fetchMe, fetchPacks]);

  const meResp = meState.resp;
  const packsResp = packsState.resp;

  const initError =
    meResp?.ok === false
      ? meResp.message || 'Failed to load billing details.'
      : packsResp?.ok === false
      ? packsResp.message || 'Failed to load credit packs.'
      : null;

  return {
    me: meResp?.ok === true ? meResp.data : null,
    packs: packsResp?.ok === true ? packsResp.data.packs : [],
    initError,
  };
}
